import { useState } from 'react';
import { api } from '../../lib/api/client.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * The SIP password for a seat: shown on demand, or replaced.
 *
 * Both actions are gated on `extension:credentials:reveal`, which sits in the
 * step-up list — so the API can refuse with a step-up challenge even though the
 * session is perfectly valid. That is not an error state to report; it is a
 * question to ask. This component asks it inline and retries, so revealing a
 * password is one interaction rather than a dead end and a trip elsewhere.
 */

interface Credential {
  username: string;
  /** The bare number, while it still signs in with this password (docs/26 E8). */
  legacy_username?: string | null;
  realm: string;
  password: string | null;
  recoverable?: boolean;
  reason?: string;
}

export function SipCredential({ tenantId, extensionId, number }: {
  tenantId: string; extensionId: string; number: string;
}) {
  const [cred, setCred] = useState<Credential | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [what, setWhat] = useState<'reveal' | 'rotate'>('reveal');
  /**
   * The challenge lives in the shared hook now. This component asked correctly
   * and the balance form did not, so the asking was lifted out rather than
   * copied across (ADR-019) -- there is one implementation of "prove it, then
   * carry on with what you were doing".
   */
  const step = useStepUp();

  const base = `/v1/tenants/${tenantId}/extensions/${extensionId}/credential`;

  const run = async (which: 'reveal' | 'rotate') => {
    setWhat(which);
    setBusy(true); setError(null);
    try {
      const r = await step.run(() => which === 'reveal'
        ? api.get<Credential>(base)
        : api.post<Credential>(`${base}/rotate`, {}));
      // undefined means the hook raised a challenge and is holding the action.
      if (r !== undefined) setCred(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the credential');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <StepUpPrompt step={step}
        what={what === 'rotate' ? 'Issuing a new SIP password' : 'Reading a SIP password'} />
      {error && (
        <div style={{ color: 'var(--arc-danger)', fontSize: 12.5, marginBottom: 8 }}>{error}</div>
      )}
      {cred && (
        <dl className="arc-kv" style={{ marginBottom: 10 }}>
          <dt>SIP username</dt><dd className="arc-mono">{cred.username}</dd>
          <dt>Domain</dt><dd className="arc-mono">{cred.realm}</dd>
          <dt>Password</dt>
          <dd>
            {cred.password ? (
              <span className="arc-row" style={{ gap: 8 }}>
                <span className="arc-mono" style={{ fontWeight: 600, userSelect: 'all' }}>
                  {cred.password}
                </span>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  onClick={() => void navigator.clipboard?.writeText(cred.password!)}>
                  Copy
                </button>
              </span>
            ) : (
              /* Imported credentials have only a digest behind them, which
                 cannot be reversed. Saying so is more useful than an error. */
              <span className="arc-subtle">{cred.reason ?? 'Not recoverable'}</span>
            )}
          </dd>
        </dl>
      )}
      {cred?.legacy_username && (
        <p className="arc-hint" style={{ margin: '0 0 10px' }}>
          Phones still signed in as {cred.legacy_username} keep working until a new password is issued.
        </p>
      )}

      <div className="arc-row" style={{ gap: 8 }}>
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
          disabled={busy} onClick={() => void run('reveal')}>
          <Icon name="search" size={13} /> {cred ? 'Show again' : 'Show password'}
        </button>
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm arc-btn-danger-ghost"
          disabled={busy}
          onClick={() => {
            if (confirm(
              `Issue a new SIP password for ${number}?\n\n`
              + 'The handset using the current password will stop registering '
              + 'within about a minute and has to be reconfigured.')) {
              void run('rotate');
            }
          }}>
          Issue a new password
        </button>
      </div>

    </div>
  );
}
